import React from 'react'
import {Redirect, useHistory, useLocation} from 'react-router-dom'
import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '../../utils/chakraMonomorphic'

import {PageHeader} from '../../components/PageHeader/PageHeader'
import {PageTemplate} from '../../templates/PageTemplate/PageTemplate'

import copy from './copy.json'
import {FACIAL_RECOGNITION_TAB_COMPONENTS} from './tabs'
import {useFacialRecognitionTabs} from './useFacialRecognitionTabs'

/**
 * Campminder-staff-only hub for facial recognition tools, one tab per tool.
 *
 * The route wrapper in Router.tsx only mounts this page once the user is staff
 * and at least one tab flag is on, so here we only decide which tabs show. Each
 * tab has its own path, and an address that names no visible tab lands on the
 * first one.
 */
export const FacialRecognitionView: React.FC = () => {
  const {visibleTabs} = useFacialRecognitionTabs()
  const {pathname} = useLocation()
  const history = useHistory()

  if (visibleTabs.length === 0) return null

  const selectedIndex = visibleTabs.findIndex(
    (tab) => pathname === tab.path || pathname.startsWith(`${tab.path}/`),
  )
  if (selectedIndex === -1) return <Redirect to={visibleTabs[0].path} />

  const pageHeader = (
    <PageHeader
      title={copy.title}
      logContext='FacialRecognition'
      description={copy.description}
    />
  )

  return (
    <PageTemplate
      header={pageHeader}
      width={['100%']}
      // Face Off's photos need the whole screen, as the local Face Off app has.
      maxWidth={
        visibleTabs[selectedIndex].id === 'faceOff' ? ['100%'] : ['1030px']
      }
      borderX={['0px']}>
      <Flex direction='column' px='.75em'>
        <Box borderTop='1px' mt={3} mb={6} />
        <Tabs
          isLazy
          colorScheme='secondary'
          index={selectedIndex}
          onChange={(index: number): void => {
            history.push(visibleTabs[index].path)
          }}>
          <TabList>
            {visibleTabs.map((tab) => (
              <Tab key={tab.id}>{tab.label}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {visibleTabs.map((tab) => {
              const TabComponent = FACIAL_RECOGNITION_TAB_COMPONENTS[tab.id]
              return (
                <TabPanel key={tab.id} px={0}>
                  <TabComponent />
                </TabPanel>
              )
            })}
          </TabPanels>
        </Tabs>
      </Flex>
    </PageTemplate>
  )
}
